import type { SqliteAsyncConnection } from '../../shared/localDb';
import { catalogSnapshotVerboseLog } from './catalogSnapshotVerbose';
import type {
  RemoteSnapshotStockRow,
  RemoteSnapshotStockTransferRow,
  RemoteSnapshotStockLotRow,
} from '../domain/catalogSnapshotTypes';

export type { RemoteSnapshotStockRow, RemoteSnapshotStockTransferRow, RemoteSnapshotStockLotRow };

const mergeLotRemainingOnSnapshotConflict = (currentRemaining: number, incomingRemaining: number) =>
  incomingRemaining < currentRemaining ? incomingRemaining : currentRemaining;

/**
 * Merge snapshot stocks by branch+product+variant cell (no collapsing variant rows into parent).
 */
export function mergeRemoteSnapshotStocks(rows: RemoteSnapshotStockRow[]): RemoteSnapshotStockRow[] {
  const byKey = new Map<string, RemoteSnapshotStockRow>();
  for (const s of rows) {
    if (!s?.id || !s.product_id || !s.branch_id) continue;
    const qty = typeof s.quantity === 'number' ? s.quantity : Number(s.quantity) || 0;
    const minQ = typeof s.min_quantity === 'number' ? s.min_quantity : Number(s.min_quantity) || 0;
    const avg = typeof s.avg_cost === 'number' ? s.avg_cost : Number(s.avg_cost) || 0;
    const exp =
      s.expires_at == null || s.expires_at === ''
        ? null
        : typeof s.expires_at === 'string'
          ? s.expires_at
          : null;
    const variantKey = s.variant_id?.trim() ? s.variant_id.trim() : 'parent';
    const key = `${s.branch_id}:${s.product_id}:${variantKey}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        ...s,
        quantity: qty,
        min_quantity: minQ,
        avg_cost: avg,
        expires_at: exp,
      });
      continue;
    }

    const eq = typeof existing.quantity === 'number' ? existing.quantity : Number(existing.quantity) || 0;
    const eMin =
      typeof existing.min_quantity === 'number' ? existing.min_quantity : Number(existing.min_quantity) || 0;
    const eAvg =
      typeof existing.avg_cost === 'number' ? existing.avg_cost : Number(existing.avg_cost) || 0;
    const totalQty = eq + qty;
    const mergedAvg = totalQty > 0 ? (eq * eAvg + qty * avg) / totalQty : 0;
    const mergedMin = Math.max(eMin, minQ);
    let mergedExp: string | null =
      existing.expires_at == null || existing.expires_at === ''
        ? null
        : typeof existing.expires_at === 'string'
          ? existing.expires_at
          : null;
    if (exp) {
      if (!mergedExp || exp < mergedExp) mergedExp = exp;
    }

    const canonicalId = existing.id <= s.id ? existing.id : s.id;

    byKey.set(key, {
      ...existing,
      id: canonicalId,
      quantity: totalQty,
      min_quantity: mergedMin,
      avg_cost: mergedAvg,
      expires_at: mergedExp,
    });
  }
  return Array.from(byKey.values());
}

const buildBatchPlaceholders = (rows: number, columns: number): string =>
  Array.from({ length: rows }, () => `(${Array(columns).fill('?').join(', ')})`).join(', ');

const STOCK_BATCH_SIZE = 20;
const TRANSFER_BATCH_SIZE = 20;
const LOT_BATCH_SIZE = 20;

export async function applyRemoteCatalogSnapshotStockWrites(
  _conn: SqliteAsyncConnection,
  params: {
    stocks: RemoteSnapshotStockRow[];
    stockTransfers: RemoteSnapshotStockTransferRow[];
    stockLots: RemoteSnapshotStockLotRow[];
  }
): Promise<void> {
  // ADR-017 Fase 4: SQLite local eliminado — dead code bajo stub no-op.
  const { stocks, stockTransfers, stockLots } = params;
  catalogSnapshotVerboseLog(
    `[catalogSnapshot][stock] Apply no-op: ${stocks.length} stocks, ${stockTransfers.length} transfers, ${stockLots.length} lots`
  );
}
